<template>
  <header
    ref="headerEl"
    :class="[
      'shared-header',
      themeClass,
      { 'shared-header--sticky': headerIsSticky },
      { 'shared-header--hidden': headerIsHidden },
      { 'shared-header--transparent': headerIsTransparent },
      { 'shared-header--always-visible': props.alwaysVisible },
    ]"
    role="banner"
  >
    <div class="shared-header__inner">
      <slot name="logo">
        <router-link
          to="/"
          class="shared-header__logo"
          aria-label="Mylimni"
        >
          <img
            src="/images/logo/logo-wobg.png"
            alt="Mylimni"
            width="140"
            height="40"
            loading="eager"
          >
        </router-link>
      </slot>

      <nav
        class="shared-header__nav"
        aria-label="Primary navigation"
      >
        <template
          v-for="link in activeLinks"
          :key="link.labelKey"
        >
          <router-link
            v-if="link.to"
            :to="link.to"
            class="shared-header__link"
            @click="closeMobileMenu"
          >
            {{ t(link.labelKey) }}
          </router-link>
          <a
            v-else-if="link.href"
            :href="link.href"
            class="shared-header__link"
            target="_blank"
            rel="noreferrer"
          >
            {{ t(link.labelKey) }}
          </a>
          <a
            v-else
            :href="`#${link.id}`"
            class="shared-header__link shared-header__link--ghost"
            @click.prevent="handleScrollToSection(link.id)"
          >
            {{ t(link.labelKey) }}
          </a>
        </template>
      </nav>

      <div class="shared-header__actions">
        <slot name="actions">
          <template v-if="showDefaultActions">
            <button
              type="button"
              class="shared-header__cta"
              data-tianji-event="header_cta_click"
              @click="handlePrimaryAction"
            >
              {{ t(config.ctaLabelKey) }}
            </button>

            <button
              type="button"
              class="shared-header__mode-toggle"
              :aria-pressed="props.audience === 'manager'"
              data-tianji-event="header_mode_switch"
              @click="handleModeSwitch"
            >
              {{ t(switchLabel) }}
            </button>
          </template>
        </slot>
      </div>

      <button
        type="button"
        class="shared-header__menu-btn"
        :aria-expanded="isMobileMenuOpen"
        aria-label="Toggle navigation menu"
        @click="toggleMobileMenu"
      >
        <span :class="['shared-header__menu-icon', { 'is-open': isMobileMenuOpen }]">
          <span />
          <span />
          <span />
        </span>
      </button>
    </div>
  </header>

  <Transition name="shared-header-mobile">
    <div
      v-if="isMobileMenuOpen"
      :class="['shared-header__mobile', themeClass]"
      role="dialog"
      aria-modal="true"
    >
      <nav class="shared-header__mobile-nav">
        <template
          v-for="link in activeLinks"
          :key="`mobile-${link.labelKey}`"
        >
          <router-link
            v-if="link.to"
            :to="link.to"
            class="shared-header__mobile-link"
            @click="closeMobileMenu"
          >
            {{ t(link.labelKey) }}
          </router-link>
          <a
            v-else-if="link.href"
            :href="link.href"
            class="shared-header__mobile-link"
            target="_blank"
            rel="noreferrer"
            @click="closeMobileMenu"
          >
            {{ t(link.labelKey) }}
          </a>
          <a
            v-else
            :href="`#${link.id}`"
            class="shared-header__mobile-link"
            @click.prevent="handleScrollToSection(link.id)"
          >
            {{ t(link.labelKey) }}
          </a>
        </template>
      </nav>

      <slot name="mobile-actions">
        <div
          v-if="showDefaultActions"
          class="shared-header__mobile-actions"
        >
          <button
            type="button"
            class="shared-header__cta shared-header__cta--mobile"
            @click="handlePrimaryAction"
          >
            {{ t(config.ctaLabelKey) }}
          </button>
          <button
            type="button"
            class="shared-header__mode-toggle shared-header__mode-toggle--mobile"
            @click="handleModeSwitch"
          >
            {{ t(switchLabel) }}
          </button>
        </div>
      </slot>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, type PropType } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHeaderConfig, type HeaderAudience, type HeaderLink } from '@/composables/useHeaderConfig'
import { useHeaderScroll } from '@/composables/useHeaderScroll'
import { tianjiService } from '@/services/tianji.service'

const props = defineProps({
  audience: {
    type: String as PropType<HeaderAudience>,
    default: 'manager',
  },
  theme: {
    type: String as PropType<'landing' | 'public'>,
    default: 'landing',
  },
  links: {
    type: Array as PropType<HeaderLink[]>,
    default: undefined,
  },
  alwaysVisible: {
    type: Boolean,
    default: false,
  },
  // The default slot content is the platform's own marketplace/audience-switch
  // CTAs — org mini-sites opt out so visitors aren't routed away from the org.
  showDefaultActions: {
    type: Boolean,
    default: true,
  },
})

const { t } = useI18n()
const router = useRouter()
const { config, switchLabel, switchRoute } = useHeaderConfig(props.audience)
const { isSticky, isHidden, isTransparent } = useHeaderScroll()

const isMobileMenuOpen = ref(false)
const headerEl = ref<HTMLElement | null>(null)

const themeClass = computed(() => (props.theme === 'public' ? 'shared-header--public' : 'shared-header--landing'))
const activeLinks = computed(() =>
  props.links && props.links.length ? props.links : config.value.links,
)
const headerIsSticky = computed(() => props.alwaysVisible || isSticky.value)
const headerIsHidden = computed(() => (props.alwaysVisible ? false : isHidden.value))
const headerIsTransparent = computed(() => (props.alwaysVisible ? false : isTransparent.value))

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
  document.body.style.overflow = ''
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : ''
}

const handlePrimaryAction = async () => {
  closeMobileMenu()
  tianjiService.track('header_cta_click', {
    audience: props.audience,
    theme: props.theme,
  })
  await router.push(config.value.ctaRoute)
}

const handleModeSwitch = async () => {
  closeMobileMenu()
  tianjiService.track('header_mode_switch', {
    audience: props.audience,
    theme: props.theme,
    target: switchRoute.value,
  })
  await router.push(switchRoute.value)
}

const handleScrollToSection = (sectionId?: string) => {
  if (!sectionId) return
  const target = document.getElementById(sectionId)
  if (!target || !headerEl.value) return
  const headerHeight = headerEl.value.offsetHeight
  const top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 16
  window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
  closeMobileMenu()
}
</script>

<style scoped>
:global(.shared-header) {
  --header-bg: transparent;
  --header-text: #fff;
  --header-primary: var(--landing-primary);
}

.shared-header {
  position: fixed;
  inset: 0 auto auto 0;
  z-index: var(--landing-z-fixed);
  width: 100%;
  background: var(--header-bg);
  color: var(--header-text);
  transition: transform 250ms ease, background 250ms ease;
  backdrop-filter: blur(12px);
}

.shared-header--sticky {
  background: rgba(255, 255, 255, 0.9);
  color: var(--landing-neutral-900);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15);
}

.shared-header--hidden {
  transform: translateY(-100%);
}

.shared-header--always-visible {
  position: sticky;
  top: 0;
  background: rgba(255, 255, 255, 0.9);
  color: var(--public-text-primary, var(--landing-neutral-900));
  border-bottom: 1px solid var(--public-border-color, var(--landing-border-color));
  box-shadow: none;
}

.shared-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: var(--landing-container-max);
  padding: var(--landing-space-4) var(--landing-container-px);
  margin: 0 auto;
}

.shared-header__nav {
  display: none;
}

.shared-header--public {
  --header-primary: var(--public-primary);
}

.shared-header--landing {
  --header-primary: var(--landing-primary);
}

.shared-header__link {
  margin: 0 var(--landing-space-4);
  font-weight: 600;
  color: inherit;
  background: none;
  border: none;
  cursor: pointer;
}

.shared-header__link--ghost {
  background: transparent;
}

.shared-header__actions {
  /* Hidden on mobile — these same actions are duplicated in the slide-out
     .shared-header__mobile-actions panel. Without this, they rendered inline
     next to the hamburger button, overflowing/wrapping the header on small
     screens and pushing its real height past what the hero's layout expects. */
  display: none;
  align-items: center;
  gap: var(--landing-space-3);
}

.shared-header__cta {
  padding: var(--landing-space-3) var(--landing-space-6);
  border-radius: var(--landing-radius-xl);
  border: none;
  font-weight: 600;
  background: var(--header-primary);
  color: #fff;
  cursor: pointer;
}

.shared-header__mode-toggle {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.6);
  color: inherit;
  border-radius: var(--landing-radius-lg);
  padding: var(--landing-space-3) var(--landing-space-4);
}

.shared-header__menu-btn {
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
}

.shared-header__menu-icon {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.shared-header__menu-icon span {
  display: block;
  width: 24px;
  height: 2px;
  background: currentColor;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.shared-header__menu-icon.is-open span:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}

.shared-header__menu-icon.is-open span:nth-child(2) {
  opacity: 0;
}

.shared-header__menu-icon.is-open span:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

.shared-header__mobile {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: var(--landing-bg-primary);
  padding: var(--landing-space-6);
  display: flex;
  flex-direction: column;
  gap: var(--landing-space-6);
  z-index: calc(var(--landing-z-fixed) + 1);
}

.shared-header__mobile-nav {
  display: flex;
  flex-direction: column;
  gap: var(--landing-space-3);
}

.shared-header__mobile-link {
  font-size: var(--landing-text-lg);
  font-weight: 600;
  background: none;
  border: none;
  color: var(--landing-text-primary);
  text-align: left;
}

.shared-header__mobile-actions {
  display: flex;
  flex-direction: column;
  gap: var(--landing-space-3);
}

.shared-header__cta--mobile {
  width: 100%;
}

.shared-header__mode-toggle--mobile {
  width: 100%;
}

@media (min-width: 1024px) {
  .shared-header__nav {
    display: flex;
  }

  .shared-header__actions {
    display: flex;
  }

  .shared-header__menu-btn {
    display: none;
  }

  .shared-header__mobile {
    display: none;
  }
}
</style>
